import { useState } from "react";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import { X, Mail, Phone, Trash2, StickyNote, History, ClipboardList, Send, UserCog, CheckCircle2, ArrowRightLeft, FilePlus2, MessageSquare, MailCheck } from "lucide-react";
import { STAGES, STAGE_META, StageBadge, Avatar, inputCls, btnPrimary, fmtDateTime, relTime, slug, useIsMobile } from "./crm";
import EmailLog from "./EmailLog";

const ACT_ICON = { created: FilePlus2, assigned: ArrowRightLeft, status: CheckCircle2, note: MessageSquare, email: Mail };

function Stepper({ status, onChange }) {
  const idx = STAGES.indexOf(status);
  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid="lead-stage-stepper">
      {STAGES.map((s, i) => {
        const m = STAGE_META[s];
        const current = s === status;
        const done = i < idx && status !== "Lost";
        return (
          <button key={s} onClick={() => onChange(s)} data-testid={`lead-status-${slug(s)}`} title={m.hint}
            className={`rounded-lg border px-2 py-2 text-[11px] font-semibold leading-tight transition-colors ${current ? `${m.badge} ring-1 ring-current` : done ? "border-slate-200 bg-slate-50 text-slate-500" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
            {s}
          </button>
        );
      })}
    </div>
  );
}

function Notes({ lead, onAdd, onDelete }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try { await onAdd(body.trim()); setBody(""); } finally { setBusy(false); }
  };
  const notes = [...(lead.notes || [])].reverse();
  return (
    <div data-testid="lead-notes">
      <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Log a call, text, or meeting… (Ctrl/⌘+Enter to save)" data-testid="lead-note-input"
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit(e); }}
          className={`${inputCls} resize-none`} />
        <div className="mt-2 flex justify-end">
          <button type="submit" disabled={busy || !body.trim()} data-testid="lead-note-submit" className={`${btnPrimary} px-3.5 py-2 text-xs`}><Send className="h-3.5 w-3.5" /> Add note</button>
        </div>
      </form>
      <ul className="mt-4 space-y-3">
        {notes.length === 0 && <li className="text-center text-xs text-slate-400 py-4">No notes yet. Keep the team in sync by logging every touchpoint.</li>}
        {notes.map((n) => (
          <li key={n.id} className="group rounded-lg border border-slate-200 bg-white p-3" data-testid={`lead-note-${n.id}`}>
            <p className="whitespace-pre-wrap text-sm text-slate-800">{n.body}</p>
            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
              <span><span className="font-semibold text-slate-700">{n.author}</span> · {fmtDateTime(n.created_at)}</span>
              <button onClick={() => onDelete(n.id)} data-testid={`lead-note-delete-${n.id}`} className="rounded p-1.5 text-slate-400 transition-opacity hover:text-rose-600 sm:opacity-0 sm:group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Activity({ items }) {
  const list = [...(items || [])].reverse();
  return (
    <ol className="relative ml-2 border-l border-slate-200 pl-5" data-testid="lead-activity">
      {list.map((a) => {
        const Icon = ACT_ICON[a.type] || History;
        return (
          <li key={a.id} className="relative pb-5 last:pb-0">
            <span className="absolute -left-[29px] flex h-4 w-4 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500"><Icon className="h-2.5 w-2.5" /></span>
            <p className="text-sm text-slate-800">{a.message}</p>
            <p className="mt-0.5 text-[11px] text-slate-500">{a.actor} · {fmtDateTime(a.created_at)}</p>
          </li>
        );
      })}
    </ol>
  );
}

function Answers({ answers }) {
  if (!answers?.length) return <p className="text-center text-xs text-slate-400 py-4">No application answers recorded.</p>;
  return (
    <dl className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="lead-answers">
      {answers.map((a, i) => (
        <div key={i} className="px-4 py-3">
          <dt className="text-[11px] font-medium text-slate-500">{a.question}</dt>
          <dd className="mt-0.5 text-sm font-semibold text-slate-900">{a.answer}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function RecruitDrawer({ lead, agents, onClose, onStage, onReassign, onAddNote, onDeleteNote, onDelete, onLoadEmails, canManage = true }) {
  const [tab, setTab] = useState("notes");
  const [confirm, setConfirm] = useState(false);
  const isMobile = useIsMobile();
  const dragControls = useDragControls();
  const tabs = [["notes", "Notes", StickyNote, lead?.notes?.length], ["emails", "E-mails", MailCheck, null], ["activity", "Activity", History, lead?.activity?.length], ["answers", "Application", ClipboardList, lead?.answers?.length]];
  const motionProps = isMobile
    ? { initial: { y: "100%" }, animate: { y: 0 }, exit: { y: "100%" }, drag: "y", dragListener: false, dragControls, dragConstraints: { top: 0, bottom: 0 }, dragElastic: { top: 0, bottom: 0.6 }, onDragEnd: (_, info) => { if (info.offset.y > 120 || info.velocity.y > 600) onClose(); } }
    : { initial: { x: "100%" }, animate: { x: 0 }, exit: { x: "100%" } };

  return (
    <AnimatePresence>
      {lead && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-[80] bg-slate-900/40 backdrop-blur-[2px]" data-testid="lead-drawer-overlay" />
          <motion.aside {...motionProps} transition={{ type: "spring", stiffness: 320, damping: 34 }}
            className="crm-root fixed inset-x-0 bottom-0 top-[max(2.5rem,env(safe-area-inset-top))] z-[90] flex flex-col rounded-t-2xl bg-white shadow-2xl sm:inset-y-0 sm:left-auto sm:right-0 sm:top-0 sm:w-full sm:max-w-lg sm:rounded-none" data-testid="lead-drawer" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
            <div className="sm:hidden touch-none" onPointerDown={(e) => dragControls.start(e)} data-testid="sheet-drag-handle"><div className="sheet-handle" /></div>
            <div className="flex items-start gap-3 sm:gap-4 border-b border-slate-200 px-4 sm:px-5 py-3 sm:py-4">
              <Avatar name={lead.full_name} size={isMobile ? "md" : "lg"} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-base sm:text-lg font-extrabold text-slate-900" data-testid="lead-drawer-name" data-selectable>{lead.full_name}</h2>
                  <StageBadge stage={lead.status} testid="lead-drawer-stage" />
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{lead.source || "Quiz Funnel"} · applied {relTime(lead.created_at)}</p>
                <div className="mt-3 hidden sm:flex flex-wrap gap-2">
                  <a href={`tel:${lead.phone}`} data-testid="lead-phone-link" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300"><Phone className="h-3.5 w-3.5 text-sky-600" /> <span className="font-mono-num">{lead.phone}</span></a>
                  <a href={`mailto:${lead.email}`} data-testid="lead-email-link" className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300"><Mail className="h-3.5 w-3.5 text-sky-600" /> <span className="truncate">{lead.email}</span></a>
                </div>
              </div>
              <button onClick={onClose} data-testid="lead-drawer-close" className="flex h-10 w-10 sm:h-9 sm:w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-900"><X className="h-4 w-4" /></button>
            </div>

            {/* Native-style quick actions (phones) */}
            <div className="grid grid-cols-3 gap-2 border-b border-slate-200 px-4 py-3 sm:hidden" data-testid="lead-quick-actions">
              {[["Call", Phone, `tel:${lead.phone}`, "lead-quick-call"], ["Text", MessageSquare, `sms:${lead.phone}`, "lead-quick-text"], ["Email", Mail, `mailto:${lead.email}`, "lead-quick-email"]].map(([label, Icon, href, tid]) => (
                <a key={label} href={href} data-testid={tid} className="flex flex-col items-center justify-center gap-1 rounded-xl bg-slate-50 border border-slate-200 py-2.5 text-[11px] font-semibold text-slate-700 active:bg-sky-50 active:border-sky-200">
                  <Icon className="h-4 w-4 text-sky-600" /> {label}
                </a>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain pb-safe">
              <div className="space-y-5 border-b border-slate-200 px-4 sm:px-5 py-4 sm:py-5">
                <div>
                  <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Pipeline stage</div>
                  <Stepper status={lead.status} onChange={(s) => onStage(lead.id, s)} />
                </div>
                {!isMobile ? null : (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Phone</div><div className="font-mono-num mt-0.5 font-semibold text-slate-800" data-selectable>{lead.phone}</div></div>
                    <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">E-mail</div><div className="mt-0.5 truncate font-semibold text-slate-800" data-selectable>{lead.email}</div></div>
                  </div>
                )}
                {canManage && (
                  <div>
                    <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500"><UserCog className="h-3.5 w-3.5" /> Assigned agent</div>
                    <select value={lead.assigned_agent_id || ""} onChange={(e) => onReassign(lead.id, e.target.value)} data-testid="lead-reassign-select" className={inputCls}>
                      <option value="">Unassigned</option>
                      {agents.map((a) => <option key={a.id} value={a.id}>{a.name}{a.active ? "" : " (paused)"}</option>)}
                    </select>
                    <p className="mt-1.5 text-[11px] text-slate-500">Reassigning sends the recruit's details to the new agent by e-mail.</p>
                  </div>
                )}
              </div>

              <div className="px-4 sm:px-5 pt-4">
                <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
                  {tabs.map(([k, label, Icon, n]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`lead-tab-${k}`} className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 sm:py-1.5 text-xs font-semibold transition-colors ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                      <Icon className="h-3.5 w-3.5" /> {label} {n > 0 && <span className="font-mono-num text-[10px] text-slate-400">{n}</span>}
                    </button>
                  ))}
                </div>
                <div className="py-4">
                  {tab === "notes" && <Notes lead={lead} onAdd={(b) => onAddNote(lead.id, b)} onDelete={(nid) => onDeleteNote(lead.id, nid)} />}
                  {tab === "emails" && <EmailLog leadId={lead.id} onLoad={onLoadEmails} />}
                  {tab === "activity" && <Activity items={lead.activity} />}
                  {tab === "answers" && <Answers answers={lead.answers} />}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-4 sm:px-5 py-3 pb-safe">
              <span className="font-mono-num text-[10px] text-slate-400">ID {lead.id.slice(0, 8)}</span>
              {!canManage ? (
                <span className="text-[11px] text-slate-400 text-right" data-testid="lead-agent-footer">Need this reassigned? Contact your admin.</span>
              ) : confirm ? (
                <span className="flex items-center gap-2 text-xs">
                  <span className="hidden sm:inline text-slate-600">Delete this recruit permanently?</span>
                  <button onClick={() => onDelete(lead.id)} data-testid="lead-delete-confirm" className="rounded-md bg-rose-600 px-3 py-2 font-semibold text-white hover:bg-rose-700">Delete</button>
                  <button onClick={() => setConfirm(false)} data-testid="lead-delete-cancel" className="rounded-md border border-slate-200 px-3 py-2 font-semibold text-slate-600">Cancel</button>
                </span>
              ) : (
                <button onClick={() => setConfirm(true)} data-testid="lead-delete-button" className="inline-flex min-h-[40px] items-center gap-1.5 rounded-md border border-slate-200 px-3 text-xs font-semibold text-slate-600 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /> Delete recruit</button>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
